import { FormattedDate } from "@edgetier/client-components";
import { FunctionComponent, memo } from "react";
import { IProps } from "./metric-comparison-chart-tooltip.types";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { addHours, addMinutes, endOfMonth, endOfWeek, format, max, startOfMonth, startOfWeek } from "date-fns";
import "./metric-comparison-chart-tooltip.scss";

/**
 * Tooltip for the metric comparison chart.
 * @param props.agentValueString   Agent's metric value.
 * @param props.dateTime           Date of metric reading.
 * @param props.overallValueString Overall metric value.
 * @param props.unitShorthand      Possible unit of the metric (seconds, percentage etc.).
 * @returns                        Chart tooltip.
 */
const MetricComparisonChartTooltip: FunctionComponent<IProps> = ({
    agentFirstName,
    agentValueString,
    dateTime,
    overallValueString,
    timePeriodGroup,
    unitShorthand,
}) => {
    return (
        <div className="metric-comparison-chart-tooltip">
            <div className="phrase-counts-chart-tooltip__date">
                {[TimePeriodGroupBy.Weeks, TimePeriodGroupBy.Months].some((group) => group !== timePeriodGroup) ? (
                    <>
                        {timePeriodGroup === TimePeriodGroupBy.Weeks && (
                            <>
                                <FormattedDate dateTime={max([startOfWeek(dateTime, { weekStartsOn: 1 }), dateTime])} />
                                &ndash;
                                <FormattedDate dateTime={max([endOfWeek(dateTime, { weekStartsOn: 1 }), dateTime])} />
                            </>
                        )}
                        {timePeriodGroup === TimePeriodGroupBy.Months && (
                            <>
                                <FormattedDate dateTime={max([startOfMonth(dateTime), dateTime])} />
                                &ndash;
                                <FormattedDate dateTime={max([endOfMonth(dateTime), dateTime])} />
                            </>
                        )}
                    </>
                ) : (
                    <FormattedDate dateTime={dateTime} />
                )}
            </div>
            {timePeriodGroup === TimePeriodGroupBy.Minutes && (
                <div className="phrase-counts-chart-tooltip__time">
                    {format(dateTime, "p")} &ndash; {format(addMinutes(dateTime, 1), "p")}
                </div>
            )}
            {timePeriodGroup === TimePeriodGroupBy.Hours && (
                <div className="phrase-counts-chart-tooltip__time">
                    {format(dateTime, "p")} &ndash; {format(addHours(dateTime, 1), "p")}
                </div>
            )}
            {timePeriodGroup === TimePeriodGroupBy.Days && (
                <div className="phrase-counts-chart-tooltip__time">
                    <FormattedDate dateTime={dateTime} />
                </div>
            )}
            <div className="metric-comparison-chart-tooltip__data">
                {typeof agentFirstName === "string" && (
                    <div className="metric-comparison-chart-tooltip__datum">
                        <div className="metric-comparison-chart-tooltip__label">{agentFirstName}</div>
                        <div className="metric-comparison-chart-tooltip__value">
                            {agentValueString}
                            {unitShorthand}
                        </div>
                    </div>
                )}

                <div className="metric-comparison-chart-tooltip__datum">
                    <div className="metric-comparison-chart-tooltip__label">
                        {typeof agentFirstName === "string" ? "Others" : "All Agents"}
                    </div>
                    <div className="metric-comparison-chart-tooltip__value">
                        {overallValueString}
                        {unitShorthand}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(MetricComparisonChartTooltip);
